import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  CreditCard,
  MessageCircle,
  Shield,
  BarChart3,
  Users,
  Clock,
  ArrowRight,
  CheckCircle,
  Zap,
  Building2,
  Phone,
  FileSpreadsheet,
  BellRing,
  Lock,
  Star,
  TrendingUp,
  ChevronRight,
  Globe,
  Headphones,
  Menu,
  X,
  Crown,
  Infinity,
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const features = [
  {
    icon: CreditCard,
    title: 'Ödeme Sözü Yönetimi',
    desc: 'Müşterilerinizden alınan ödeme sözlerini dijital ortamda kaydedin. Tutar, vade tarihi ve notları tek ekrandan takip edin.',
  },
  {
    icon: MessageCircle,
    title: 'WhatsApp Entegrasyonu',
    desc: 'QR kod ile WhatsApp Business hattınızı bağlayın. Ödeme bildirimleri ve hatırlatmalar otomatik gönderilsin.',
  },
  {
    icon: BellRing,
    title: 'Akıllı Hatırlatmalar',
    desc: 'Vade günü yaklaşan ve geçen ödemeler için otomatik hatırlatma mesajları. Tahsilat oranınızı yükseltin.',
  },
  {
    icon: BarChart3,
    title: 'Canlı Dashboard',
    desc: 'Toplam alacak, bugün vadesi gelen ödemeler, tahsilat performansı gibi kritik verileri anlık izleyin.',
  },
  {
    icon: Users,
    title: 'Ekip Yönetimi',
    desc: 'Personellerinize cari ataması yapın. Herkes kendi sorumluluğundaki müşterileri ve ödemeleri görsün.',
  },
  {
    icon: FileSpreadsheet,
    title: 'Excel Entegrasyonu',
    desc: 'Mevcut müşteri listenizi Excel ile toplu aktarın. İstediğiniz zaman verilerinizi dışa aktarın.',
  },
  {
    icon: Lock,
    title: 'Veri Güvenliği',
    desc: 'Her firma izole ortamda çalışır. Verileriniz şifreli bağlantılarla korunur, sadece siz erişirsiniz.',
  },
  {
    icon: Globe,
    title: 'Her Yerden Erişim',
    desc: 'Mobil uyumlu tasarım sayesinde telefon, tablet veya bilgisayardan her an sisteme ulaşın.',
  },
];

const steps = [
  {
    num: '01',
    title: 'Kayıt Olun',
    desc: 'Firma bilgilerinizi girin, 30 saniyede hesabınızı oluşturun. 7 gün ücretsiz deneme başlasın.',
    icon: Building2,
  },
  {
    num: '02',
    title: 'WhatsApp Bağlayın',
    desc: 'Panelden QR kodu okutarak WhatsApp Business hattınızı sisteme entegre edin.',
    icon: Phone,
  },
  {
    num: '03',
    title: 'Carileri Ekleyin',
    desc: 'Müşterilerinizi tek tek veya Excel ile toplu olarak sisteme aktarın.',
    icon: Users,
  },
  {
    num: '04',
    title: 'Tahsilata Başlayın',
    desc: 'Ödeme sözlerini kaydedin, otomatik hatırlatmalar çalışsın, tahsilatınız hızlansın.',
    icon: TrendingUp,
  },
];

const stats = [
  { value: '500+', label: 'Aktif Firma' },
  { value: '%40', label: 'Tahsilat Artışı' },
  { value: '50.000+', label: 'Gönderilen Mesaj' },
  { value: '7/24', label: 'Sistem Erişimi' },
];

const faqs = [
  {
    q: 'Sistemi kullanmak için ne gerekiyor?',
    a: 'Sadece bir internet bağlantısı ve WhatsApp Business hattı yeterli. Herhangi bir yazılım kurulumu gerekmez, tarayıcı üzerinden çalışır.',
  },
  {
    q: 'Deneme süresi boyunca tüm özellikler açık mı?',
    a: 'Evet, 7 günlük deneme süresinde tüm özelliklere sınırsız erişim sağlarsınız. Kredi kartı bilgisi gerekmez.',
  },
  {
    q: 'WhatsApp mesajları nasıl gönderiliyor?',
    a: 'Kendi WhatsApp Business hattınızı QR kod ile bağlarsınız. Mesajlar sizin hattınızdan, kendi adınıza gönderilir.',
  },
  {
    q: 'Verilerim güvende mi?',
    a: 'Her firma izole bir ortamda çalışır. Verilerinize sadece siz ve yetkilendirdiğiniz personeller erişebilir. Tüm bağlantılar SSL ile şifrelenir.',
  },
  {
    q: 'Kaç personel ekleyebilirim?',
    a: 'Paketinize göre personel limiti belirlenir. Başlangıç paketinde 5 personele kadar ekleme yapabilirsiniz.',
  },
  {
    q: 'Mevcut müşteri listemi aktarabilir miyim?',
    a: 'Evet, Excel dosyası ile toplu müşteri aktarımı yapabilirsiniz. Şablonu sistemden indirip doldurmanız yeterli.',
  },
];

const testimonials = [
  {
    name: 'Ahmet Y.',
    role: 'Finans Müdürü',
    company: 'ABC Ticaret A.Ş.',
    text: 'Tahsilat süremiz ortalama 15 günden 9 güne düştü. WhatsApp hatırlatmaları müşterilerimizi harekete geçiriyor.',
  },
  {
    name: 'Fatma K.',
    role: 'İşletme Sahibi',
    company: 'FK Mobilya',
    text: 'Eskiden Excel tablolarıyla uğraşıyorduk. Şimdi tek panelden tüm ödeme sözlerini takip ediyoruz.',
  },
  {
    name: 'Mehmet D.',
    role: 'Muhasebe Sorumlusu',
    company: 'Demir İnşaat Ltd.',
    text: 'Personellerime cari ataması yapabiliyorum. Herkes kendi müşterisinden sorumlu, iş akışımız çok düzeldi.',
  },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [mobileMenu, setMobileMenu] = useState(false);
  const [packages, setPackages] = useState([]);

  useEffect(() => {
    axios.get(`${API_URL}/api/packages`).then(res => setPackages(res.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      {/* Navbar */}
      <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <Building2 className="h-5 w-5" />
            </div>
            <div>
              <span className="font-heading text-lg font-bold tracking-tight">ÖdemeTakip</span>
              <span className="hidden sm:inline text-[10px] text-muted-foreground ml-1.5">by WebPlace</span>
            </div>
          </div>
          <nav className="hidden items-center gap-6 md:flex">
            <a href="#ozellikler" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Özellikler</a>
            <a href="#nasil-calisir" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Nasıl Çalışır</a>
            <a href="#fiyat" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Fiyatlandırma</a>
            <a href="#sss" className="text-sm text-muted-foreground hover:text-foreground transition-colors">SSS</a>
          </nav>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" className="hidden sm:inline-flex" onClick={() => navigate('/login')} data-testid="landing-login-btn">
              Giriş Yapın
            </Button>
            <Button size="sm" onClick={() => navigate('/register')} data-testid="landing-register-btn">
              Ücretsiz Deneyin
            </Button>
            <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setMobileMenu(!mobileMenu)}>
              {mobileMenu ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>
        {mobileMenu && (
          <div className="border-t px-4 py-3 md:hidden animate-in slide-in-from-top-2">
            <nav className="flex flex-col gap-2">
              <a href="#ozellikler" onClick={() => setMobileMenu(false)} className="rounded-lg px-3 py-2 text-sm hover:bg-muted">Özellikler</a>
              <a href="#nasil-calisir" onClick={() => setMobileMenu(false)} className="rounded-lg px-3 py-2 text-sm hover:bg-muted">Nasıl Çalışır</a>
              <a href="#fiyat" onClick={() => setMobileMenu(false)} className="rounded-lg px-3 py-2 text-sm hover:bg-muted">Fiyatlandırma</a>
              <a href="#sss" onClick={() => setMobileMenu(false)} className="rounded-lg px-3 py-2 text-sm hover:bg-muted">SSS</a>
              <Button variant="outline" className="mt-1" onClick={() => { setMobileMenu(false); navigate('/login'); }}>Giriş Yapın</Button>
            </nav>
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,hsl(var(--primary)/0.06),transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,hsl(var(--primary)/0.04),transparent_60%)]" />
        <div className="relative mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20 lg:py-28">
          <div className="mx-auto max-w-3xl text-center">
            <Badge variant="outline" className="mb-6 gap-2 px-4 py-1.5 text-sm border-emerald-200 bg-emerald-50" data-testid="hero-badge">
              <Zap className="h-3.5 w-3.5 text-emerald-500" />
              WhatsApp ile otomatik tahsilat hatırlatma
            </Badge>
            <h1 className="font-heading text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl leading-[1.1]">
              Ödeme Sözlerini Takip Edin,{' '}
              <span className="bg-gradient-to-r from-primary via-emerald-600 to-emerald-500 bg-clip-text text-transparent">
                Tahsilatınızı Hızlandırın
              </span>
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-base text-muted-foreground sm:text-lg leading-relaxed">
              Müşterilerinizden alınan ödeme sözlerini dijital ortamda kaydedin.
              Vade tarihlerinde otomatik WhatsApp hatırlatmaları ile tahsilat sürenizi kısaltın.
            </p>
            <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <Button size="lg" className="w-full sm:w-auto text-base px-8 h-12" onClick={() => navigate('/register')} data-testid="hero-register-btn">
                7 Gün Ücretsiz Başlayın
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button variant="outline" size="lg" className="w-full sm:w-auto text-base px-8 h-12" onClick={() => navigate('/login')} data-testid="hero-login-btn">
                Giriş Yapın
              </Button>
            </div>
            <div className="mt-6 flex items-center justify-center gap-6 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5"><CheckCircle className="h-4 w-4 text-emerald-500" />Kredi kartı gerekmez</span>
              <span className="flex items-center gap-1.5"><CheckCircle className="h-4 w-4 text-emerald-500" />Kurulum yok</span>
              <span className="hidden sm:flex items-center gap-1.5"><CheckCircle className="h-4 w-4 text-emerald-500" />Anında başlayın</span>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="border-y bg-slate-900">
        <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
          <div className="grid grid-cols-2 gap-6 sm:grid-cols-4">
            {stats.map((s, i) => (
              <div key={s.label} className="text-center">
                <p className={`font-heading text-2xl font-bold sm:text-3xl ${i % 2 === 0 ? 'text-emerald-400' : 'text-white'}`}>{s.value}</p>
                <p className="mt-1 text-sm text-slate-400">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 sm:py-24" id="ozellikler">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="mx-auto max-w-2xl text-center">
            <Badge variant="outline" className="mb-4 border-emerald-200 bg-emerald-50 text-emerald-700">Özellikler</Badge>
            <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">
              Tahsilat Yönetiminde <span className="text-emerald-600">İhtiyacınız Olan</span> Her Şey
            </h2>
            <p className="mt-3 text-muted-foreground">
              Küçük ve orta ölçekli işletmeler için tasarlanmış, kapsamlı tahsilat takip platformu.
            </p>
          </div>
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((f, i) => (
              <div
                key={f.title}
                className="group rounded-xl border bg-background p-5 transition-all duration-300 hover:shadow-lg hover:shadow-primary/5 hover:-translate-y-1 hover:border-primary/30"
              >
                <div className={`mb-3 flex h-10 w-10 items-center justify-center rounded-lg transition-colors ${i % 2 === 0 ? 'bg-primary/10 text-primary group-hover:bg-primary group-hover:text-white' : 'bg-emerald-100 text-emerald-600 group-hover:bg-emerald-500 group-hover:text-white'}`}>
                  <f.icon className="h-5 w-5" />
                </div>
                <h3 className="font-heading font-semibold">{f.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="border-t bg-muted/30 py-16 sm:py-24" id="nasil-calisir">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="mx-auto max-w-2xl text-center">
            <Badge variant="outline" className="mb-4 border-primary/30 bg-primary/5 text-primary">Nasıl Çalışır</Badge>
            <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">
              4 Basit Adımda Tahsilatınızı <span className="text-primary">Dijitalleştirin</span>
            </h2>
          </div>
          <div className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {steps.map((s, i) => (
              <div key={s.num} className="relative" data-testid={`step-${s.num}`}>
                <div className="mb-4 flex items-center gap-3">
                  <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white ${i % 2 === 0 ? 'bg-primary' : 'bg-emerald-500'}`}>{s.num}</span>
                  {s.num !== '04' && <ChevronRight className="hidden lg:block h-5 w-5 text-muted-foreground/40" />}
                </div>
                <h3 className="font-heading font-semibold">{s.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="mx-auto max-w-2xl text-center">
            <Badge variant="outline" className="mb-4 border-emerald-200 bg-emerald-50 text-emerald-700">Müşteri Yorumları</Badge>
            <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">
              Kullanıcılarımız <span className="text-emerald-600">Ne Diyor?</span>
            </h2>
          </div>
          <div className="mt-12 grid gap-6 sm:grid-cols-3">
            {testimonials.map((t) => (
              <div key={t.name} className="rounded-xl border bg-background p-6">
                <div className="mb-3 flex gap-1">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-sm leading-relaxed text-muted-foreground">"{t.text}"</p>
                <div className="mt-4 border-t pt-4">
                  <p className="font-medium text-sm">{t.name}</p>
                  <p className="text-xs text-muted-foreground">{t.role}, {t.company}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="border-t bg-muted/30 py-16 sm:py-24" id="fiyat">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="mx-auto max-w-xl text-center">
            <Badge variant="outline" className="mb-4 border-primary/30 bg-primary/5 text-primary">Fiyatlandırma</Badge>
            <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">
              Basit ve <span className="text-emerald-600">Şeffaf</span> Fiyatlandırma
            </h2>
            <p className="mt-3 text-muted-foreground">Önce deneyin, beğenirseniz abone olun. Tüm paketler yıllık faturalandırılır.</p>
          </div>
          <div className="mx-auto mt-10 grid gap-5 max-w-5xl sm:grid-cols-2 lg:grid-cols-4" data-testid="pricing-grid">
            {packages.map((pkg) => {
              const yearlyPrice = pkg.yearly_price || (pkg.monthly_price * 12);
              const isPopular = pkg.slug === 'profesyonel';
              const isEnterprise = pkg.slug === 'kurumsal';
              const isTrial = pkg.slug === 'trial';
              return (
                <div
                  key={pkg.id}
                  className={`relative rounded-2xl bg-background p-6 transition-all duration-200 hover:shadow-lg hover:-translate-y-1 ${isPopular ? 'border-2 border-primary shadow-lg shadow-primary/10' : 'border'}`}
                  data-testid={`pricing-card-${pkg.slug}`}
                >
                  {isPopular && <Badge className="absolute -top-3 left-1/2 -translate-x-1/2">En Popüler</Badge>}
                  {isEnterprise && <Badge variant="secondary" className="absolute -top-3 left-1/2 -translate-x-1/2">Tam Paket</Badge>}
                  <p className={`text-sm font-medium uppercase tracking-wider ${isPopular ? 'text-primary' : 'text-muted-foreground'}`}>
                    {pkg.name}
                  </p>
                  {isTrial ? (
                    <>
                      <div className="mt-3 flex items-baseline gap-1">
                        <span className="font-heading text-3xl sm:text-4xl font-bold">0 TL</span>
                      </div>
                      <p className="text-sm text-muted-foreground">/ 7 gün deneme</p>
                    </>
                  ) : (
                    <>
                      <div className="mt-3 flex items-baseline gap-1">
                        <span className="font-heading text-2xl sm:text-3xl font-bold">{yearlyPrice.toLocaleString('tr-TR')} TL</span>
                      </div>
                      <p className="text-sm text-muted-foreground">/ yıl</p>
                      <p className="text-xs text-primary font-medium mt-1">Aylık {pkg.monthly_price.toLocaleString('tr-TR')} TL</p>
                    </>
                  )}
                  <div className="mt-3 flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Users className="h-3.5 w-3.5" />
                      {pkg.is_unlimited_personnel ? 'Sınırsız' : pkg.personnel_limit} Çalışan
                    </span>
                    <span className="flex items-center gap-1">
                      <MessageCircle className="h-3.5 w-3.5" />
                      {pkg.is_unlimited_messages ? 'Sınırsız' : pkg.monthly_message_limit} Mesaj
                    </span>
                  </div>
                  <ul className="mt-5 space-y-2.5 text-sm">
                    {pkg.features?.map((f) => (
                      <li key={f} className="flex items-start gap-2">
                        <CheckCircle className={`mt-0.5 h-4 w-4 shrink-0 ${isPopular ? 'text-primary' : 'text-emerald-500'}`} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    variant={isPopular ? 'default' : 'outline'}
                    className="mt-6 w-full"
                    size="lg"
                    onClick={() => navigate('/register')}
                    data-testid={`pricing-btn-${pkg.slug}`}
                  >
                    {isTrial ? 'Ücretsiz Başlayın' : 'Hemen Başlayın'}
                    {!isTrial && <ArrowRight className="ml-2 h-4 w-4" />}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-24" id="sss">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <div className="text-center">
            <Badge variant="outline" className="mb-4 border-emerald-200 bg-emerald-50 text-emerald-700">SSS</Badge>
            <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">
              Sıkça Sorulan <span className="text-primary">Sorular</span>
            </h2>
          </div>
          <Accordion type="single" collapsible className="mt-10">
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-${i}`}>
                <AccordionTrigger className="text-left font-medium">{faq.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">{faq.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="border-t py-16 sm:py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="rounded-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-8 text-center sm:p-14 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-[300px] h-[300px] rounded-full bg-emerald-500/10 blur-[100px]" />
            <div className="absolute bottom-0 left-0 w-[200px] h-[200px] rounded-full bg-primary/20 blur-[80px]" />
            <div className="relative z-10">
              <h2 className="font-heading text-2xl font-bold text-white sm:text-3xl">
                Tahsilatınızı Hızlandırmaya <span className="text-emerald-400">Hazır Mısınız?</span>
              </h2>
              <p className="mx-auto mt-3 max-w-lg text-slate-300">
                Hemen kayıt olun, 7 gün boyunca tüm özellikleri ücretsiz deneyin.
                Kredi kartı gerekmez.
              </p>
              <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
                <Button size="lg" className="w-full sm:w-auto px-8 h-12 text-base bg-emerald-500 hover:bg-emerald-600 text-white" onClick={() => navigate('/register')} data-testid="cta-register-btn">
                  Ücretsiz Deneyin
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button size="lg" variant="outline" className="w-full sm:w-auto px-8 h-12 text-base border-white/20 text-white hover:bg-white/10" onClick={() => navigate('/login')} data-testid="cta-login-btn">
                  Giriş Yapın
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t bg-muted/30 py-10">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <div className="flex items-center gap-2.5">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                  <Building2 className="h-4 w-4" />
                </div>
                <span className="font-heading font-bold">ÖdemeTakip</span>
              </div>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                WhatsApp entegreli tahsilat yönetim platformu. Ödeme sözlerini takip edin, otomatik hatırlatmalar gönderin.
              </p>
            </div>
            <div>
              <h4 className="font-medium text-sm mb-3">Hızlı Erişim</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="#ozellikler" className="hover:text-foreground transition-colors">Özellikler</a></li>
                <li><a href="#fiyat" className="hover:text-foreground transition-colors">Fiyatlandırma</a></li>
                <li><a href="#sss" className="hover:text-foreground transition-colors">SSS</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-sm mb-3">Yasal</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="/legal/kvkk" className="hover:text-foreground transition-colors">KVKK Aydınlatma Metni</a></li>
                <li><a href="/legal/gizlilik" className="hover:text-foreground transition-colors">Gizlilik Politikası</a></li>
                <li><a href="/legal/uyelik" className="hover:text-foreground transition-colors">Üyelik Sözleşmesi</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-sm mb-3">İletişim</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2"><Headphones className="h-4 w-4" />info@odemetakibi.com</li>
                <li className="flex items-center gap-2"><Phone className="h-4 w-4" />+90 216 606 99 24</li>
                <li className="flex items-center gap-2"><MessageCircle className="h-4 w-4" /><a href="https://wa.me/902166069924" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">WhatsApp Destek</a></li>
              </ul>
            </div>
          </div>
          <div className="mt-8 border-t pt-6 text-center text-sm text-muted-foreground">
            &copy; 2026 ÖdemeTakip. Tüm hakları saklıdır.
          </div>
        </div>
      </footer>
    </div>
  );
}
